import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { useUnsavedChangesSource } from '../appUpdate/unsavedChanges';
import type { StorePreorderGoLiveInput, StorePreorderGoLiveSetting, StorePreorderGoLiveView } from '../../types/storePreorderGoLive';

/** The editor's working copy: dates as `YYYY-MM-DD` or '' for none. */
export interface StorePreorderGoLiveDraft {
  opensAt: string;
  availabilityDate: string;
  availabilityNote: string;
  orderNow: boolean;
}

export type StorePreorderGoLiveProblem = 'noteRequired' | 'datePassed' | 'dateOrder' | null;

export interface StorePreorderGoLiveState {
  productId: string;
  loading: boolean;
  error: boolean;
  view: StorePreorderGoLiveView | undefined;
  draft: StorePreorderGoLiveDraft;
  dirty: boolean;
  problem: StorePreorderGoLiveProblem;
  saving: boolean;
  set: <K extends keyof StorePreorderGoLiveDraft>(key: K, value: StorePreorderGoLiveDraft[K]) => void;
  clear: () => void;
  save: () => Promise<unknown>;
  discard: () => void;
}

export const EMPTY_GO_LIVE_DRAFT: StorePreorderGoLiveDraft = { opensAt: '', availabilityDate: '', availabilityNote: '', orderNow: false };

export const draftFromSetting = (setting: StorePreorderGoLiveSetting | null | undefined): StorePreorderGoLiveDraft =>
  setting
    ? {
        opensAt: setting.opensAt ?? '',
        availabilityDate: setting.availabilityDate ?? '',
        availabilityNote: setting.availabilityNote ?? '',
        orderNow: setting.orderNow === true,
      }
    : EMPTY_GO_LIVE_DRAFT;

/** The PUT body for a draft: null when nothing is set, so the setting is cleared. */
export const inputFromDraft = (draft: StorePreorderGoLiveDraft): StorePreorderGoLiveInput | null => {
  const note = draft.availabilityNote.trim();
  const input: StorePreorderGoLiveInput = {
    opensAt: draft.orderNow ? null : draft.opensAt || null,
    availabilityDate: draft.availabilityDate || null,
    availabilityNote: note || null,
    orderNow: draft.orderNow,
  };
  return input.opensAt || input.availabilityDate || input.availabilityNote || input.orderNow ? input : null;
};

/** Today as `YYYY-MM-DD` in the browser's zone. */
const localToday = (): string => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/** The same rules the API applies (owner decision default: Take orders now needs a date or note). */
export const goLiveDraftProblem = (draft: StorePreorderGoLiveDraft, today: string = localToday()): StorePreorderGoLiveProblem => {
  if (draft.orderNow && !draft.availabilityNote.trim() && !draft.availabilityDate) return 'noteRequired';
  // A date stands in for the note only while it is ahead; the API checks it in the store's own zone.
  if (draft.orderNow && !draft.availabilityNote.trim() && draft.availabilityDate <= today) return 'datePassed';
  if (!draft.orderNow && draft.opensAt && draft.availabilityDate && draft.availabilityDate < draft.opensAt) return 'dateOrder';
  return null;
};

const sameDraft = (a: StorePreorderGoLiveDraft, b: StorePreorderGoLiveDraft) =>
  JSON.stringify(inputFromDraft(a)) === JSON.stringify(inputFromDraft(b));

/**
 * The store's pre-order go-live for one product (flag `store_preorder_go_live`, MOT-766), edited
 * locally and saved in one PUT. Called by the product details dialog, not by the editor, so an
 * unsaved go-live stays guarded (`useUnsavedChangesSource`) while another tab is open.
 */
export function useStorePreorderGoLive(productId: string | undefined, enabled: boolean): StorePreorderGoLiveState | null {
  const queryClient = useQueryClient();
  const queryKey = ['store-preorder-go-live', productId];
  const query = useQuery({
    queryKey,
    queryFn: () => apiService.getStorePreorderGoLive(productId!),
    enabled: enabled && !!productId,
    staleTime: 30_000,
  });
  const saved = useMemo(() => draftFromSetting(query.data?.setting), [query.data]);
  const [draft, setDraft] = useState<StorePreorderGoLiveDraft>(saved);
  // A fresh read (another product, or after a save) resets the draft.
  useEffect(() => setDraft(saved), [saved]);

  const mutation = useMutation({
    mutationFn: (next: StorePreorderGoLiveDraft) => apiService.saveStorePreorderGoLive(productId!, inputFromDraft(next)),
    onSuccess: (view: StorePreorderGoLiveView) => queryClient.setQueryData(queryKey, view),
  });

  const problem = goLiveDraftProblem(draft);
  const dirty = enabled && !!query.data?.canEdit && !sameDraft(draft, saved);
  const save = () => (problem ? Promise.reject(new Error('Fix the go-live before saving.')) : mutation.mutateAsync(draft));
  const discard = () => setDraft(saved);
  useUnsavedChangesSource({ dirty, save, discard });

  if (!enabled || !productId) return null;
  return {
    productId,
    loading: query.isLoading,
    error: query.isError,
    view: query.data,
    draft,
    dirty,
    problem,
    saving: mutation.isPending,
    set: (key, value) => setDraft((prev) => ({ ...prev, [key]: value })),
    clear: () => setDraft(EMPTY_GO_LIVE_DRAFT),
    save,
    discard,
  };
}
